import React, { useState, useEffect } from 'react';
import { X, Link, Eye, EyeOff } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import Modal from './ui/modal';
import {
  AdsService,
  Ad,
  CreateAdRequest,
  UpdateAdRequest,
} from '../services/ads';

interface AdFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  ad?: Ad | null;
  isEdit?: boolean;
}

export const AdFormModal: React.FC<AdFormModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  ad,
  isEdit = false,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    destinationUrl: '',
    description: '',
    userTrackingEnabled: true,
    active: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (isEdit && ad) {
        setFormData({
          name: ad.name,
          destinationUrl: ad.destinationUrl,
          description: ad.description || '',
          userTrackingEnabled: ad.userTrackingEnabled,
          active: ad.active,
        });
      } else {
        setFormData({
          name: '',
          destinationUrl: '',
          description: '',
          userTrackingEnabled: true,
          active: true,
        });
      }
      setError(null);
    }
  }, [isOpen, isEdit, ad]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const validateForm = (): string | null => {
    if (!formData.name.trim()) {
      return 'Ad name is required';
    }
    if (!formData.destinationUrl.trim()) {
      return 'Destination URL is required';
    }

    // Basic URL validation
    try {
      new URL(formData.destinationUrl);
    } catch {
      return 'Please enter a valid URL (including http:// or https://)';
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isEdit && ad) {
        const updateData: UpdateAdRequest = {
          name: formData.name,
          destinationUrl: formData.destinationUrl,
          description: formData.description || undefined,
          userTrackingEnabled: formData.userTrackingEnabled,
          active: formData.active,
        };
        await AdsService.updateAd(ad.id, updateData);
      } else {
        const createData: CreateAdRequest = {
          name: formData.name,
          destinationUrl: formData.destinationUrl,
          description: formData.description || undefined,
        };
        await AdsService.createAd(createData);
      }

      onSuccess();
    } catch (error: any) {
      console.error('Failed to save ad:', error);
      setError(error.response?.data?.message || 'Failed to save ad');
    } finally {
      setLoading(false);
    }
  };

  const generatePreviewUrl = () => {
    if (!ad || !ad.trackingUrl) return '';
    return AdsService.generateTrackingUrl(ad, 'sample-user-123', {
      utm_source: 'email',
      utm_medium: 'newsletter',
      utm_campaign: 'spring-sale',
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Ad' : 'Create New Link'}
    >
      <div>
        <form onSubmit={handleSubmit} className='space-y-4'>
          {error && (
            <div className='alert-enhanced alert-error'>
              <span className='text-red-500'>⚠️</span>
              <span>{error}</span>
            </div>
          )}

          <div className='form-field'>
            <Label htmlFor='name' className='form-label'>
              Ad Name *
            </Label>
            <Input
              id='name'
              type='text'
              value={formData.name}
              onChange={e => handleInputChange('name', e.target.value)}
              placeholder='e.g., Summer Campaign 2025'
              className='form-input focus-ring'
              required
            />
          </div>

          <div className='form-field'>
            <Label htmlFor='destinationUrl' className='form-label'>
              Destination URL *
            </Label>
            <div className='relative'>
              <Link className='absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400' />
              <Input
                id='destinationUrl'
                type='url'
                value={formData.destinationUrl}
                onChange={e =>
                  handleInputChange('destinationUrl', e.target.value)
                }
                placeholder='https://example.com/landing-page'
                className='form-input focus-ring pl-10'
                required
              />
            </div>
            <p className='text-sm text-gray-600 mt-1'>
              Where users will be redirected when they click the tracking link
            </p>
          </div>

          <div className='form-field'>
            <Label htmlFor='description' className='form-label'>
              Description
            </Label>
            <textarea
              id='description'
              value={formData.description}
              onChange={e => handleInputChange('description', e.target.value)}
              placeholder='Optional description for this link'
              className='form-input focus-ring resize-none'
              rows={3}
            />
          </div>

          <div className='form-field'>
            <div className='flex items-center space-x-2'>
              <input
                type='checkbox'
                id='userTrackingEnabled'
                checked={formData.userTrackingEnabled}
                onChange={e =>
                  handleInputChange('userTrackingEnabled', e.target.checked)
                }
                className='rounded border-gray-300 text-blue-600 focus:ring-blue-500'
              />
              <Label htmlFor='userTrackingEnabled' className='form-label'>
                Enable User Tracking
              </Label>
            </div>
            <p className='text-sm text-gray-600'>
              Collect user analytics for this link
            </p>
          </div>

          {isEdit && ad && (
            <div className='bg-gray-50 p-4 rounded-lg'>
              <div className='flex items-center justify-between mb-2'>
                <h4 className='font-medium text-gray-900'>
                  Generated Tracking URL
                </h4>
                {/* <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  onClick={() => setShowPreview(!showPreview)}
                >
                  {showPreview ? (
                    <EyeOff className='w-4 h-4' />
                  ) : (
                    <Eye className='w-4 h-4' />
                  )}
                </Button> */}
              </div>
              <div className='space-y-2'>
                <code className='block text-xs bg-white p-2 rounded border text-gray-700 break-all'>
                  {ad.trackingUrl}
                </code>
                {/* {showPreview && (
                  <div>
                    <p className='text-sm font-medium text-gray-700 mb-1'>
                      Example with UTM parameters:
                    </p>
                    <code className='block text-xs bg-white p-2 rounded border text-gray-700 break-all'>
                      {generatePreviewUrl()}
                    </code>
                  </div>
                )} */}
              </div>
            </div>
          )}

          <div className='flex justify-end space-x-3 pt-4 border-t border-gray-200'>
            <Button
              type='button'
              variant='ghost'
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type='submit' className='btn-enhanced' disabled={loading}>
              {loading ? 'Saving...' : isEdit ? 'Update Link' : 'Create Link'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
